import { useEffect, useState, useRef } from 'react';

export const useIntersectionObserver = (
    targetElement: string,
    singleUse?: boolean,
    rootMargin?: number,
    threshold?: number,
    rootContainer?: string,
) => {
    const [isElementVisible, setIsElementVisible] = useState<boolean | null>(null);
    const isMountedRef = useRef(true);
    const observerRef = useRef<IntersectionObserver | MutationObserver | null>(null);
    const isUsingMutationObserverRef = useRef(false);
    const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isResizingRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {

        if (targetElement.trim() === '' || (singleUse && isElementVisible)) {
            return;
        }

        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
            isUsingMutationObserverRef.current = false;
        }

        const options = {
            root: rootContainer ? document.querySelector(`.${rootContainer}`) : null,
            rootMargin: rootMargin ? `${rootMargin}px` : '0px',
            threshold: threshold || 0,
        };

        const attachIntersectionObserver = (element: Element) => {
            const intersectionObserver = new IntersectionObserver((entries) => {
                if (isMountedRef.current && !isResizingRef.current) {
                    entries.forEach((entry) => {
                        const rect = entry.boundingClientRect;
                        const isValidRect = Object.values(rect.toJSON()).some(x => x !== 0);
                        
                        if (!isValidRect) {
                            if (observerRef.current) {
                                observerRef.current.disconnect();
                                observerRef.current = null;
                            }
                            const currentElement = document.querySelector(`.${targetElement}`);
                            if (currentElement) {
                                attachIntersectionObserver(currentElement);
                            }
                            return;
                        }
                        setIsElementVisible(entry.isIntersecting);
                    });
                }
            }, options);

            intersectionObserver.observe(element);
            observerRef.current = intersectionObserver;
        };

        let element = document.querySelector(`.${targetElement}`);

        if (element) {
            attachIntersectionObserver(element);
            isUsingMutationObserverRef.current = false;
        } else {
            const mutationObserver = new MutationObserver(() => {
                element = document.querySelector(`.${targetElement}`);
                const rect = element?.getBoundingClientRect();
                if (element && Object.values(rect.toJSON()).some(x => x !== 0) && isUsingMutationObserverRef.current) {
                    isUsingMutationObserverRef.current = false;
                    mutationObserver.disconnect();
                    observerRef.current = null;
                    attachIntersectionObserver(element);
                }
            });

            const watchRoot = rootContainer ? document.querySelector(`.${rootContainer}`) : document.body;
            mutationObserver.observe(watchRoot || document.body, {
                childList: true,
                subtree: true,
            });
            observerRef.current = mutationObserver;
            isUsingMutationObserverRef.current = true;
        }

        const debouncedHandleResize = () => {
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            
            isResizingRef.current = true;
            
            resizeTimeoutRef.current = setTimeout(() => {
                const element = document.querySelector(`.${targetElement}`);
                if (!element) {
                    isResizingRef.current = false;
                    return;
                }
                
                const rect = element.getBoundingClientRect();
                const isValidRect = Object.values(rect.toJSON()).some(x => x !== 0);
                
                if (!isValidRect) {
                    isResizingRef.current = false;
                    return;
                }
                
                if (observerRef.current) {
                    observerRef.current.disconnect();
                    observerRef.current = null;
                    isUsingMutationObserverRef.current = false;
                }
                attachIntersectionObserver(element);
                isResizingRef.current = false;
            }, 150);
        };

        window.addEventListener('resize', debouncedHandleResize);

        return () => {
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            observerRef.current?.disconnect();
            observerRef.current = null;
            isUsingMutationObserverRef.current = false;
            window.removeEventListener('resize', debouncedHandleResize);
        };
    }, [targetElement, singleUse, rootMargin, threshold, rootContainer]);

    return { isElementVisible };
};
